import React, { useState } from 'react';
import Nav from './Nav';

function VerifyEmailBanner({ userStatus, userEmail }) {
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  if (!userStatus || userStatus.email_verified !== false || !userEmail) return null;

  const handleResend = async () => {
    setResendState('sending');
    try {
      await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail }),
      });
    } catch {}
    setResendState('sent');
  };

  return (
    <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-wrap items-center gap-3 text-sm text-amber-800">
      <span className="text-xl">📬</span>
      <span className="flex-1 min-w-[200px]">
        Please confirm your email — we sent a link to <strong>{userEmail}</strong>. Check your spam folder if you don't see it.
      </span>
      {resendState === 'sent' ? (
        <span className="font-medium text-green-700">Sent! Check your inbox.</span>
      ) : (
        <button
          onClick={handleResend}
          disabled={resendState === 'sending'}
          className="font-semibold underline disabled:opacity-60"
        >
          {resendState === 'sending' ? 'Sending...' : 'Resend email'}
        </button>
      )}
    </div>
  );
}

const sermonPrepTools = [
  { id: 'sermon',     icon: '📖', title: 'Sermon Preparation',  desc: 'Generate full sermon outlines anchored to any Scripture passage.' },
  { id: 'biblestudy', icon: '📚', title: 'Bible Study Builder',  desc: 'Create complete small group or Sunday school lesson guides.' },
  { id: 'series',     icon: '🗓️', title: 'Series Planner',       desc: 'Plan a cohesive multi-week sermon series with a unified arc.' },
  { id: 'devotional', icon: '✍️', title: 'Devotional Writer',    desc: 'Create daily devotionals with Scripture, reflection, and prayer.' },
];
const commCareTools = [
  { id: 'announcement', icon: '📢', title: 'Announcement Writer', desc: 'Create social, bulletin, and email announcements for any event.' },
  { id: 'discussion',   icon: '👥', title: 'Discussion Builder',  desc: 'Build complete small group discussion guides from your sermon.' },
  { id: 'pastoral',     icon: '🤝', title: 'Pastoral Care',       desc: 'Compose compassionate, Scripture-anchored messages for sensitive situations.' },
  { id: 'prayer',       icon: '🙏', title: 'Prayer Response',     desc: 'Write warm, personal responses to written prayer requests.' },
  { id: 'evangelism',   icon: '💬', title: 'Evangelism Guide',    desc: 'Create natural conversation guides for sharing the gospel.' },
  { id: 'eventpromo',   icon: '🎉', title: 'Event Promo',         desc: 'Generate a full multi-channel promotion package for any event.' },
];

function ToolCard({ tool, onSelectTool }) {
  return (
    <div className="rounded-xl border border-gray-200 p-6 flex flex-col bg-white hover:shadow-md transition-shadow">
      <div className="text-4xl mb-4">{tool.icon}</div>
      <h3 className="font-semibold text-lg mb-2" style={{ color: '#1B3A5C' }}>{tool.title}</h3>
      <p className="text-gray-500 text-sm flex-1 mb-6">{tool.desc}</p>
      <button
        onClick={() => onSelectTool(tool.id)}
        className="w-full py-2 rounded-lg text-white font-medium transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#C8921A' }}
      >
        Open Tool
      </button>
    </div>
  );
}

function UsageBar({ userStatus, onNavigate }) {
  if (!userStatus) return null;
  const { role, generations_used_this_month: used, generations_limit: limit } = userStatus;

  if (role === 'admin') return null;

  if (role === 'pro') {
    return (
      <div className="mt-10 flex justify-center">
        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
          ✓ Pastor Pro — Unlimited Generations
        </span>
      </div>
    );
  }

  // Free tier
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const atLimit = used >= limit;
  const barColor = atLimit ? '#EF4444' : used >= 4 ? '#F59E0B' : '#C8921A';
  const textColor = atLimit ? 'text-red-600' : used >= 4 ? 'text-amber-600' : 'text-gray-600';

  return (
    <div className="mt-10 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${textColor}`}>
          {atLimit
            ? 'Monthly limit reached — upgrade for unlimited access'
            : `${used} of ${limit} free generations used this month`}
        </span>
        <span className="text-xs text-gray-400">{limit - used > 0 ? `${limit - used} remaining` : '0 remaining'}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div
          className="h-2.5 rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      {atLimit && (
        <div className="mt-3 text-center">
          <button
            onClick={() => onNavigate('upgrade')}
            className="text-sm font-semibold px-5 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#C8921A' }}
          >
            Upgrade to Pastor Pro →
          </button>
        </div>
      )}
      {!atLimit && (
        <p className="text-xs text-gray-400 mt-2">Resets on the 1st of each month. <button onClick={() => onNavigate('upgrade')} className="hover:underline" style={{ color: '#C8921A' }}>Upgrade for unlimited →</button></p>
      )}
    </div>
  );
}

export default function Dashboard({ onSelectTool, onNavigate, onLogout, userRole, currentPage, userEmail, userStatus }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Nav onNavigate={onNavigate} onLogout={onLogout} userRole={userRole} currentPage={currentPage} />

      <main className="flex-1 px-6 py-12 max-w-6xl mx-auto w-full">
        <VerifyEmailBanner userStatus={userStatus} userEmail={userEmail} />
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#1B3A5C' }}>Your Tools</h2>
        <p className="text-gray-500 mb-10">Choose a tool to get started.</p>

        <div className="mb-10">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Sermon Preparation</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {sermonPrepTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} onSelectTool={onSelectTool} />
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Communication & Care</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {commCareTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} onSelectTool={onSelectTool} />
            ))}
          </div>
        </div>

        <UsageBar userStatus={userStatus} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
